import _ from "lodash";
import moment from "moment";

/**
 * Collapses historics rows of any date cadence (daily, weekly, monthly, or a
 * mix within the same series) into one row per calendar month, anchored on
 * day 15 to match the historic monthly convention. Existing chart/summary
 * components assume ~1 row per month; this keeps that contract regardless of
 * how fine-grained the underlying data becomes.
 *
 * `sum_*` fields (including `sum_boolean_*`) are added across the days that
 * fall in the month. `avg_weight_by_units_*` fields are re-weighted by each
 * row's own `sum_units`, which is a valid re-aggregation since each row is
 * itself already a units-weighted average over its own sub-period.
 * `groupByKeys` groups by extra dimensions (e.g. `company_id`) in addition to
 * the month, so unrelated series aren't merged together.
 */
export const resampleHistoricsToMonthly = (historics, groupByKeys = []) => {
  if (!historics || historics.length === 0) return [];

  const groups = _.groupBy(historics, (historic) => {
    const monthKey = moment(historic.date).format("YYYY-MM");
    const extraKey = groupByKeys.map((key) => historic[key]).join("|");
    return `${monthKey}|${extraKey}`;
  });

  return _.map(groups, (rows) => {
    const totalUnits = _.sumBy(rows, "sum_units");
    const monthlyHistoric = {
      ...rows[0],
      date: moment(rows[0].date)
        .startOf("month")
        .date(15)
        .format("YYYY-MM-DD"),
    };

    Object.keys(rows[0]).forEach((key) => {
      if (key.startsWith("sum_")) {
        monthlyHistoric[key] = _.sumBy(rows, key);
      } else if (key.startsWith("avg_weight_by_units_")) {
        monthlyHistoric[key] = totalUnits
          ? _.sumBy(rows, (row) => (row[key] || 0) * (row.sum_units || 0)) /
            totalUnits
          : _.meanBy(rows, key);
      }
    });

    return monthlyHistoric;
  });
};

const getReducedHistorics = (historics) => {
  return _.reduce(
    historics,
    (totals, historic) => {
      return {
        units: totals.units + historic.sum_units,
        units_in: totals.units_in + historic.sum_units_in,
        margin: totals.margin + historic.sum_margin,
        sales_in: totals.sales_in + historic.sum_sales_in,
        sales: totals.sales + historic.sum_sales,
        promotions: historic.sum_boolean_is_promotion
          ? totals.promotions + historic.sum_boolean_is_promotion
          : totals.promotions,
        followed_promotions: historic.sum_boolean_is_followed
          ? totals.followed_promotions + historic.sum_boolean_is_followed
          : totals.followed_promotions,
      };
    },
    {
      units: 0,
      units_in: 0,
      margin: 0,
      sales: 0,
      sales_in: 0,
      promotions: null,
      followed_promotions: null,
    }
  );
};

export default getReducedHistorics;
